// src/components/utils/modals/ReusableModal.tsx
import React, { useRef, useEffect } from "react"
import Input from "../../ui/Input"
import Select from "../../ui/Select"
import Button from "../../ui/Button"
import { FaSave, FaTimes } from "react-icons/fa"

interface ReusableModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  fields: Array<{
    label: React.ReactNode
    value: string
    onChange: (value: string) => void
    required?: boolean
    type?: "text" | "select" | "date"
    options?: Array<{ value: string; label: string }>
    error?: string
    readOnly?: boolean
  }>
  onSave: () => void
  isSaving?: boolean
  saveButtonText?: string
  cancelButtonText?: string
  readOnlyMode?: boolean
}

const ReusableModal: React.FC<ReusableModalProps> = ({
  isOpen,
  onClose,
  title,
  fields,
  onSave,
  isSaving = false,
  saveButtonText = "Guardar",
  cancelButtonText = "Cancelar",
  readOnlyMode = false,
}) => {
  const modalRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    if (isOpen) {
      document.addEventListener("keydown", handleEscape)
      document.body.style.overflow = "hidden"
    }
    return () => {
      document.removeEventListener("keydown", handleEscape)
      document.body.style.overflow = "unset"
    }
  }, [isOpen, onClose])

  const handleClickOutside = (e: React.MouseEvent<HTMLDivElement>) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black bg-opacity-50" onClick={handleClickOutside}>
      <div ref={modalRef} className="bg-white w-full max-w-2xl max-h-[90vh] rounded-xl shadow-lg flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-[#2d53da] rounded-t-xl">
          <h3 className="text-lg font-bold text-white">{title}</h3>
          <button onClick={onClose} className="text-white hover:text-white/80 transition-colors">
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6 bg-white">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-3">
            {fields.map((field, index) => (
              <div key={index} className="w-full">
                {field.type === "select" && field.options ? (
                  <Select
                    label={field.label}
                    value={field.value}
                    onChange={field.onChange}
                    options={[{ value: "", label: "Seleccione una opción" }, ...field.options]}
                    error={field.error}
                    disabled={field.readOnly || readOnlyMode}
                    fullWidth
                  />
                ) : (
                  <Input
                    label={field.label}
                    value={field.value}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      field.onChange(e.target.value)
                    }
                    type={field.type || "text"}
                    required={field.required}
                    error={field.error}
                    disabled={field.readOnly || readOnlyMode}
                    fullWidth
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl">
          <Button variant="outline" onClick={onClose} icon={<FaTimes />}>{cancelButtonText}</Button>
          {!readOnlyMode && (
            <Button
              onClick={onSave}
              variant="primary"
              icon={<FaSave />}
              isLoading={isSaving}
              loadingText="Guardando..."
            >
              {saveButtonText}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

export default ReusableModal